// @react
import React, {
  ReactElement,
  useMemo,
  useRef,
  useEffect,
  useState,
} from 'react'
// @libraries
import Slider from 'react-slick'
import clsx from 'clsx'
import { makeStyles, createStyles, useTheme } from '@material-ui/core/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { ResponsiveObject } from 'react-slick'
import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'
// @components
import colors from '@bit/atd.web.colors'
// @module components
import Arrow from './Arrow'

const useStyles = makeStyles((theme) =>
  createStyles({
    root: {},
    slideWrapper: {
      width: '100%',
      paddingRight: '16px',
    },
    dotsWrapper: {
      position: 'relative',
      bottom: 0,
      fontSize: 0,
      '& > li': {
        margin: '0 3px !important',
        [theme.breakpoints.down('xs')]: {
          margin: '0 !important',
        },
      },
      '& .slick-active div': {
        backgroundColor: colors.ctaPrimary,
        borderColor: colors.ctaPrimary,
      },
    },
    dotsRightSpace: {
      right: '170px',
    },
    dot: {
      width: '8px',
      height: '8px',
      border: `2px solid ${colors.gray500}`,
      borderRadius: '50%',
      position: 'absolute',
      left: '50%',
      top: '50%',
      transform: 'translate(-50%, -50%)',
      [theme.breakpoints.down('xs')]: {
        borderWidth: '1px',
        width: '6px',
        height: '6px',
      },
    },
    hasControls: {
      '& .slick-list': {
        paddingBottom: '16px',
      },
      '& .slick-dots': {
        display: 'block',
        textAlign: 'right',
        paddingRight: '24px',
        paddingTop: '24px',
      },
    },
    hasShowBtnControl: {
      '& .slick-slider': {
        marginBottom: '24px',
      },
      '& .slick-dots': {
        position: 'absolute',
        right: '24px',
        width: 'auto',
        maxWidth: '300px',
        bottom: (props: { btnHeight: number }) => {
          if (!props?.btnHeight) return '-24px'
          const btnHeight = `-${props.btnHeight}px`
          const marginBottom = '24px'
          const offset = '10px'
          return `calc((${btnHeight} - ${marginBottom}) + ${offset})`
        },
      },
    },
    isItemPeek: {
      '& .slick-list': {
        padding: '0 20% 0 0 !important',
      },
    },
    isItemSameHeight: {
      '& .slick-track': {
        display: 'flex',
      },
      '& .slick-slide': {
        height: 'inherit',
      },
      '& .slick-slide > div': {
        height: '100%',
      },
      '& .slick-slide > div > div': {
        height: '100%',
      },
    },
  }),
)

type PropsType = {
  /** React Elements array */
  children: ReactElement[]
  /** Show the little orange dot controls, one per item in carousel */
  showDots?: boolean
  /** Show the left and right carousel arrows */
  showArrows?: boolean
  /** Show the left and right carousel arrows */
  showBtn?: ReactElement
  /** Same Height Items */
  sameHeight?: boolean
  /** Number of items to advance carousel when the arrows are pressed */
  advancedItems?: number
  /** Infinite scroll capability */
  infinite?: boolean
  /** Allows for next item Peek, only works when more than one item is in carousel */
  itemPeek?: boolean
  /** Allows for responsive slides */
  responsive?: ResponsiveObject[]
}

const Carousel = ({
  showDots,
  showArrows,
  showBtn,
  sameHeight,
  advancedItems,
  infinite,
  itemPeek,
  responsive,
  children,
}: PropsType): ReactElement => {
  /******************************************
   * STATE
   ******************************************/
  const [btnHeight, setBtnHeight] = useState(0)

  /******************************************
   * REFS
   ******************************************/

  const btnRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (showBtn && showDots && btnRef.current) {
      setBtnHeight(btnRef.current.clientHeight)
    }
  })

  /******************************************
   * HOOKS
   ******************************************/

  const theme = useTheme()
  const classes = useStyles({ btnHeight })
  const desktop = useMediaQuery(theme.breakpoints.up('sm'))

  /******************************************
   * SLICK SETTINGS
   ******************************************/

  const allowItemsPeek = itemPeek && children.length > 0

  const customPaging = () => <div className={classes.dot} />

  const sliderSettings = {
    centerMode: allowItemsPeek,
    className: clsx({
      [classes.isItemPeek]: allowItemsPeek,
      [classes.isItemSameHeight]: sameHeight,
    }),
    infinite,
    slidesToShow: 1,
    dots: showDots,
    arrows: showArrows,
    slidesToScroll: advancedItems,
    dotsClass: clsx(classes.dotsWrapper, 'slick-dots', {
      [classes.dotsRightSpace]: desktop && showArrows,
    }),
    prevArrow: <Arrow direction="left" />,
    nextArrow: <Arrow direction="right" />,
    customPaging,
    responsive: responsive || [],
  }

  /******************************************
   * RENDER
   ******************************************/

  const sliderItems = useMemo(
    () =>
      children.map((child, index) => {
        return (
          <div className={classes.slideWrapper} key={index}>
            {child}
          </div>
        )
      }),
    [children],
  )

  return (
    <div
      className={clsx(classes.root, {
        [classes.hasControls]: !showBtn && (showArrows || showDots),
        [classes.hasShowBtnControl]: showBtn && (showArrows || showDots),
      })}
    >
      <Slider {...sliderSettings}>{sliderItems}</Slider>

      {showBtn && <div ref={btnRef}>{showBtn}</div>}
    </div>
  )
}

Carousel.defaultProps = {
  showDots: true,
  showArrows: false,
  sameHeight: false,
  advancedItems: 2,
  infinite: true,
  itemPeek: true,
}

export default Carousel
